import { useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Check, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import { useOrgPath } from '@/lib/pathFor'
import BookingDrawer from '@/components/bookings/BookingDrawer'
import PortCombobox, { type PortSearchResult } from '@/components/ports/PortCombobox'
import { ThreadsTab, ShipmentTimelineTab } from './show/ActivityTabs'
import BookingCard from './show/BookingCard'
import DocumentsTab from './show/DocumentsTab'
import ShipmentCommandHeader from './show/ShipmentCommandHeader'
import type {
  BookingFull,
  ShipmentsShowProps,
} from './show/types'
import {
  TABS,
  type TabName,
} from './show/helpers'

// --- Main Component ---

export default function ShipmentsShow({ shipment, shipment_header, bookings, threads, shipment_documents, timeline, carriers, carrier_codes_with_api, carrier_codes_with_rate_sheets }: ShipmentsShowProps) {
  const [activeTab, setActiveTab] = useState<TabName>('Overview')
  const [documentBookingFilterId, setDocumentBookingFilterId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingBooking, setEditingBooking] = useState<BookingFull | null>(null)
  const pathFor = useOrgPath()
  const { props } = usePage<{ errors?: Record<string, string[] | string> }>()
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})
  const [selectedRouteValid, setSelectedRouteValid] = useState({ origin_port: true, destination_port: true })
  const [editForm, setEditForm] = useState<EditFormState>({
    origin_port: shipment.origin_port || '',
    destination_port: shipment.destination_port || '',
    commodity: shipment.commodity || '',
  })

  function handleSaveEdit() {
    const nextErrors: Record<string, string> = {}
    if (!selectedRouteValid.origin_port) nextErrors.origin_port = 'Select a port from the list.'
    if (!selectedRouteValid.destination_port) nextErrors.destination_port = 'Select a port from the list.'
    setLocalErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    router.patch(pathFor(`/shipments/${shipment.id}`), {
      shipment: editForm,
    }, {
      preserveState: false,
      onSuccess: () => {
        setEditing(false)
      },
      onError: () => {
        toast.error('Failed to update shipment')
      },
    })
  }

  function handleCancelEdit() {
    setEditForm({
      origin_port: shipment.origin_port || '',
      destination_port: shipment.destination_port || '',
      commodity: shipment.commodity || '',
    })
    setLocalErrors({})
    setSelectedRouteValid({ origin_port: true, destination_port: true })
    setEditing(false)
  }

  function clearLocalError(field: string) {
    setLocalErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  function handleOpenAddBooking() {
    setActiveTab('Overview')
    setEditingBooking(null)
    setDrawerOpen(true)
  }

  function handleSelectDocuments(bookingId?: string | null) {
    setDocumentBookingFilterId(bookingId || null)
    setActiveTab('Documents')
  }

  function handleOpenEditBooking(booking: BookingFull) {
    setEditingBooking(booking)
    setDrawerOpen(true)
  }

  function handleCloseBookingDrawer() {
    setDrawerOpen(false)
    setEditingBooking(null)
  }

  const untaggedDocCount = shipment_header.untagged_document_count
  const unreviewedDocCount = shipment_documents.filter((document) => document.review_outcome === 'unreviewed').length
  const documentTabWorkCount = untaggedDocCount + unreviewedDocCount
  return (
    <>
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="border-b px-8 pt-5 pb-0 space-y-4">
          <ShipmentCommandHeader
            shipmentHeader={shipment_header}
            pathFor={pathFor}
            editing={editing}
            activeTab={activeTab}
            onEdit={() => setEditing(true)}
            onAddBooking={handleOpenAddBooking}
            onSelectDocuments={() => handleSelectDocuments()}
          />

          {/* Tab Navigation */}
          <div className="flex items-end gap-0">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'Documents') setDocumentBookingFilterId(null)
                  setActiveTab(tab)
                }}
                className={`px-4 py-2.5 text-[13px] cursor-pointer border-b-2 flex items-center gap-2 ${
                  activeTab === tab
                    ? 'font-semibold text-primary border-primary'
                    : 'font-medium text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                {tab}
                {tab === 'Documents' && documentTabWorkCount > 0 && (
                  <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[11px] font-semibold rounded-full ${untaggedDocCount > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {documentTabWorkCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </header>

        {/* Tab Content */}
        <div>
          {activeTab === 'Overview' && (
            <div className="p-8 space-y-6">
              {editing ? (
                <EditShipmentForm
                  editForm={editForm}
                  setEditForm={setEditForm}
                  originPortDetails={shipment.origin_port_details}
                  destinationPortDetails={shipment.destination_port_details}
                  errors={{ ...(props.errors || {}), ...localErrors }}
                  setSelectedRouteValid={setSelectedRouteValid}
                  clearFieldError={clearLocalError}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <OverviewTab
                  bookings={bookings}
                  pathFor={pathFor}
                  shipmentId={shipment.id}
                  onOpenAdd={handleOpenAddBooking}
                  onOpenEdit={handleOpenEditBooking}
                  onSelectDocuments={handleSelectDocuments}
                />
              )}
            </div>
          )}
          {activeTab === 'Threads' && <div className="p-8"><ThreadsTab threads={threads} /></div>}
          {activeTab === 'Timeline' && <div className="p-8"><ShipmentTimelineTab events={timeline} /></div>}
          {activeTab === 'Documents' && (
            <div className="p-8">
              <DocumentsTab
                shipmentId={shipment.id}
                shipmentDocuments={shipment_documents}
                bookings={bookings}
                pathFor={pathFor}
                bookingFilterId={documentBookingFilterId}
                onBookingFilterChange={setDocumentBookingFilterId}
              />
            </div>
          )}
        </div>
      </div>
      <BookingDrawer
        shipmentId={shipment.id}
        originPort={shipment.origin_port}
        destinationPort={shipment.destination_port}
        carriers={carriers || []}
        carrierCodesWithApi={carrier_codes_with_api || []}
        carrierCodesWithRateSheets={carrier_codes_with_rate_sheets || []}
        booking={editingBooking}
        open={drawerOpen}
        onClose={handleCloseBookingDrawer}
      />
    </>
  )
}

// --- Edit Shipment Form ---

interface EditFormState extends Record<string, string> {
  origin_port: string
  destination_port: string
  commodity: string
}

function EditShipmentForm({ editForm, setEditForm, originPortDetails, destinationPortDetails, errors, setSelectedRouteValid, clearFieldError, onSave, onCancel }: {
  editForm: EditFormState
  setEditForm: Dispatch<SetStateAction<EditFormState>>
  originPortDetails: PortSearchResult | null
  destinationPortDetails: PortSearchResult | null
  errors: Record<string, string[] | string>
  setSelectedRouteValid: Dispatch<SetStateAction<{ origin_port: boolean; destination_port: boolean }>>
  clearFieldError: (field: string) => void
  onSave: () => void
  onCancel: () => void
}) {
  const fieldError = (field: string) => {
    const error = errors[field]
    return Array.isArray(error) ? error.join(', ') : error
  }
  const hasRouteError = Boolean(fieldError('origin_port') || fieldError('destination_port'))

  return (
    <div className="max-w-lg space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[13px] font-semibold">Edit Shipment</h3>
        <div className="min-h-4 flex-1 px-3 text-right">
          {hasRouteError && (
            <p role="alert" className="truncate text-[12px] font-medium text-red-600">
              Select highlighted ports from the dropdown.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs cursor-pointer" onClick={onCancel}>
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
          <Button size="sm" className="h-7 text-xs cursor-pointer" onClick={onSave}>
            <Check className="h-3 w-3 mr-1" />
            Save
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Origin Port</label>
            <PortCombobox
              value={editForm.origin_port}
              onChange={(value) => {
                setEditForm((f) => ({ ...f, origin_port: value }))
                clearFieldError('origin_port')
              }}
              onValidityChange={(valid) => setSelectedRouteValid((state) => ({ ...state, origin_port: valid }))}
              initialPort={originPortDetails}
              allowUnresolvedInitialValue
              placeholder="e.g. CNSHA"
              invalidMessage={fieldError('origin_port')}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Destination Port</label>
            <PortCombobox
              value={editForm.destination_port}
              onChange={(value) => {
                setEditForm((f) => ({ ...f, destination_port: value }))
                clearFieldError('destination_port')
              }}
              onValidityChange={(valid) => setSelectedRouteValid((state) => ({ ...state, destination_port: valid }))}
              initialPort={destinationPortDetails}
              allowUnresolvedInitialValue
              placeholder="e.g. USLAX"
              invalidMessage={fieldError('destination_port')}
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Commodity</label>
          <Input
            value={editForm.commodity}
            onChange={(e) => setEditForm((f) => ({ ...f, commodity: e.target.value }))}
            className="h-8 text-[13px]"
            placeholder="e.g. Electronics"
          />
        </div>
      </div>
    </div>
  )
}

// --- Overview Tab (Booking Cards with Document Lists) ---

function OverviewTab({ bookings, pathFor, shipmentId, onOpenAdd, onOpenEdit, onSelectDocuments }: {
  bookings: BookingFull[]
  pathFor: (path: string) => string
  shipmentId: string
  onOpenAdd: () => void
  onOpenEdit: (booking: BookingFull) => void
  onSelectDocuments: (bookingId?: string | null) => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-foreground">Bookings ({bookings.length})</h3>
        <button
          onClick={onOpenAdd}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] text-muted-foreground hover:text-foreground font-medium cursor-pointer"
        >
          <Plus className="h-3 w-3" /> Add Booking
        </button>
      </div>
      {bookings.length === 0 ? (
        <p className="text-sm text-muted-foreground">No bookings yet.</p>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              allBookings={bookings}
              pathFor={pathFor}
              onEdit={() => onOpenEdit(booking)}
              onSelectDocuments={onSelectDocuments}
              shipmentId={shipmentId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

ShipmentsShow.layout = (page: ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
